import {
  Badge,
  HStack,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Copy } from 'lucide-react';

interface CopyToClipboardPopoverProps {
  /** The value to display and copy */
  value: string;
  /** Text to display in the badge trigger (default: "+1") */
  badgeText?: string;
  /** Title for the toast notification (default: "Copied!") */
  toastTitle?: string;
  /** Custom description for the toast (if not provided, uses the value) */
  toastDescription?: string;
  /** Whether to show the full value in toast description (default: true) */
  showValueInToast?: boolean;
}

export function CopyToClipboardPopover({
  value,
  badgeText = '+1',
  toastTitle = 'Copied to clipboard',
  toastDescription,
}: CopyToClipboardPopoverProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const handleCopyClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    void navigator.clipboard.writeText(value);
    onClose();

    toast({
      title: toastTitle,
      ...(toastDescription !== null ? { description: toastDescription } : {}),
      status: 'success',
      duration: 3000,
    });
  };

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      trigger="hover"
      placement="top"
      gutter={4}
      closeOnBlur={false}
    >
      <PopoverTrigger>
        <Badge
          fontWeight="normal"
          bg="bg.muted"
          variant="subtle"
          borderRadius="full"
          cursor="pointer"
        >
          {badgeText}
        </Badge>
      </PopoverTrigger>
      <PopoverContent
        width="auto"
        py={2}
        px={3}
        fontSize="md"
        borderRadius="md"
        bg="black"
        color="white"
        border="none"
      >
        <PopoverArrow bg="black" shadowColor="transparent" />
        <HStack spacing="2">
          <Text as="span" userSelect="all">
            {value}
          </Text>
          <Copy size="16" cursor="pointer" onClick={handleCopyClick} />
        </HStack>
      </PopoverContent>
    </Popover>
  );
}
